/**
 * Custom Audio Player with Waveform Visualization
 * Renders interactive waveform bars for audio messages in PM threads
 */

const WAVEFORM_BARS_DESKTOP = 35;
const WAVEFORM_BARS_MOBILE = 20;
const BAR_MAX_HEIGHT = 28;
const BAR_MIN_HEIGHT = 4;
const POINTER_TAP_THRESHOLD_PX = 8;
const POINTER_CLICK_SUPPRESSION_MS = 500;
const KEYBOARD_SEEK_STEP_SECONDS = 5;

/**
 * Format seconds as M:SS
 */
function formatTime(seconds) {
    if (!seconds || !isFinite(seconds) || seconds < 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Format a clip's total length as M:SS.
 *
 * formatTime floors, so a clip under a second reads as 0:00 and the message looks like a
 * broken attachment. Such a clip is captioned as a second instead, the smallest non-empty
 * value the format can express.
 *
 * A stored duration is whole seconds, so a sub-second clip arrives as 0 and is
 * indistinguishable from a duration nothing has reported yet. hasClip is the discriminator:
 * a container with a source has audio to play whatever the column says, so its 0 means
 * "shorter than the column can express", while a container without one has nothing to
 * describe and keeps 0:00.
 *
 * The lift applies to the total caption only. The elapsed counter is a measurement and keeps
 * flooring, and the duration behind the progress fill, the seek mapping and aria-valuemax
 * keeps its measured value — a lifted one would leave the fill short of the end and map the
 * strip past it.
 */
function formatTotalTime(seconds, hasClip) {
    const isSubSecond = hasClip && isFinite(seconds) && seconds >= 0 && seconds < 1;
    return formatTime(isSubSecond ? 1 : seconds);
}

/**
 * Combine waveform samples into target number of bars
 */
function combineWaveform(waveform, targetBars) {
    if (!waveform || !waveform.length) return null;
    const samplesPerBar = Math.ceil(waveform.length / targetBars);
    const bars = [];
    for (let i = 0; i < waveform.length; i += samplesPerBar) {
        const chunk = waveform.slice(i, i + samplesPerBar);
        const avg = chunk.reduce((sum, v) => sum + v, 0) / chunk.length;
        bars.push(avg);
    }
    return bars;
}

/**
 * Initialize a single audio player element
 */
function initAudioPlayer(container) {
    const src = container.dataset.src;
    const hasClip = Boolean(src);
    const apiDuration = parseInt(container.dataset.duration, 10) || 0;
    const isOutgoing = container.dataset.outgoing === '1';
    let waveform = null;
    try {
        waveform = container.dataset.waveform ? JSON.parse(container.dataset.waveform) : null;
    } catch { /* ignore */ }

    const targetBars = window.innerWidth < 640 ? WAVEFORM_BARS_MOBILE : WAVEFORM_BARS_DESKTOP;
    const bars = combineWaveform(waveform, targetBars);

    // State
    let isPlaying = false;
    let currentTime = 0;
    let duration = apiDuration;
    let animFrame = null;
    let touchSeekPointer = null;
    let pointerSeekClickUntil = 0;

    // Build DOM
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    audio.src = src;

    // Play/pause button
    const playBtn = document.createElement('button');
    playBtn.type = 'button';
    playBtn.className = 'flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full hover:scale-110 active:scale-95 transition-transform duration-200';
    playBtn.innerHTML = '<i data-lucide="play" class="w-5 h-5 ml-0.5"></i>';
    playBtn.setAttribute('aria-label', 'Play');

    // Waveform or progress bar container
    const waveformContainer = document.createElement('div');
    waveformContainer.className = 'flex-1 min-w-0 cursor-pointer';
    waveformContainer.setAttribute('role', 'slider');
    waveformContainer.setAttribute('aria-valuemin', '0');
    waveformContainer.setAttribute('aria-valuemax', String(duration));
    waveformContainer.setAttribute('aria-valuenow', '0');
    waveformContainer.tabIndex = 0;

    // Time display
    const timeDisplay = document.createElement('span');
    timeDisplay.className = 'text-xs tabular-nums whitespace-nowrap flex-shrink-0 ' +
        (isOutgoing ? 'text-gray-500' : 'text-gray-500');

    // Layout — clear any server-rendered fallback (a labelled
    // placeholder shown pre-hydration so the row isn't blank) before
    // attaching the interactive player.
    while (container.firstChild) {
        container.removeChild(container.firstChild);
    }
    container.className += ' flex items-center gap-2 py-1';
    container.appendChild(audio);
    container.appendChild(playBtn);
    container.appendChild(waveformContainer);
    container.appendChild(timeDisplay);

    // Bar color classes
    const playedColor = isOutgoing ? 'bg-pink-400' : 'bg-gray-600';
    const unplayedColor = isOutgoing ? 'bg-pink-300/50' : 'bg-gray-300';
    const btnColor = isOutgoing ? 'text-pink-500' : 'text-gray-600';
    playBtn.classList.add(...btnColor.split(' '));

    // Render waveform bars or fallback progress bar
    let barElements = [];
    if (bars && bars.length > 0) {
        const barsWrapper = document.createElement('div');
        barsWrapper.className = 'flex items-center gap-px h-9';

        bars.forEach((peak) => {
            const bar = document.createElement('div');
            const height = Math.max(BAR_MIN_HEIGHT, Math.round(peak * BAR_MAX_HEIGHT));
            bar.className = `w-1.5 rounded-sm ${unplayedColor}`;
            bar.style.height = height + 'px';
            barsWrapper.appendChild(bar);
            barElements.push(bar);
        });

        waveformContainer.appendChild(barsWrapper);
    } else {
        // Fallback: simple progress bar
        const trackOuter = document.createElement('div');
        trackOuter.className = `w-full h-3 rounded-full overflow-hidden ${isOutgoing ? 'bg-pink-200/50' : 'bg-gray-200'}`;
        const trackInner = document.createElement('div');
        trackInner.className = `h-full rounded-full transition-all duration-100 ${playedColor}`;
        trackInner.style.width = '0%';
        trackOuter.appendChild(trackInner);
        waveformContainer.appendChild(trackOuter);
        barElements = []; // Use trackInner for progress
        waveformContainer._trackInner = trackInner;
    }

    // Update time display
    function updateTimeDisplay() {
        timeDisplay.textContent = formatTime(currentTime) + ' / ' + formatTotalTime(duration, hasClip);
    }

    function syncDurationFromAudio() {
        if (audio.duration && isFinite(audio.duration)) {
            duration = audio.duration;
            waveformContainer.setAttribute('aria-valuemax', String(duration));
        }
        return duration;
    }

    // Update visual progress
    function updateProgress() {
        const progress = duration > 0 ? currentTime / duration : 0;
        waveformContainer.setAttribute('aria-valuenow', String(Math.floor(currentTime)));
        waveformContainer.setAttribute('aria-valuetext', formatTime(currentTime));

        if (barElements.length > 0) {
            barElements.forEach((bar, i) => {
                const barProgress = i / barElements.length;
                if (barProgress < progress) {
                    bar.className = bar.className.replace(unplayedColor, playedColor);
                    if (!bar.className.includes(playedColor)) {
                        bar.classList.remove(...unplayedColor.split(' '));
                        bar.classList.add(...playedColor.split(' '));
                    }
                } else {
                    bar.className = bar.className.replace(playedColor, unplayedColor);
                    if (!bar.className.includes(unplayedColor)) {
                        bar.classList.remove(...playedColor.split(' '));
                        bar.classList.add(...unplayedColor.split(' '));
                    }
                }
            });
        } else if (waveformContainer._trackInner) {
            waveformContainer._trackInner.style.width = (progress * 100) + '%';
        }

        updateTimeDisplay();
    }

    // Animation loop for smooth playback progress
    function animateProgress() {
        if (!audio.paused && !audio.ended) {
            currentTime = audio.currentTime;
            updateProgress();
            animFrame = requestAnimationFrame(animateProgress);
        }
    }

    // Event handlers
    audio.addEventListener('loadedmetadata', () => {
        syncDurationFromAudio();
        updateTimeDisplay();
    });

    audio.addEventListener('play', () => {
        isPlaying = true;
        playBtn.innerHTML = '<i data-lucide="pause" class="w-5 h-5"></i>';
        window.LucideIcons?.initialize(playBtn);
        animateProgress();
    });

    audio.addEventListener('pause', () => {
        isPlaying = false;
        playBtn.innerHTML = '<i data-lucide="play" class="w-5 h-5 ml-0.5"></i>';
        window.LucideIcons?.initialize(playBtn);
        cancelAnimationFrame(animFrame);
    });

    audio.addEventListener('ended', () => {
        isPlaying = false;
        currentTime = 0;
        playBtn.innerHTML = '<i data-lucide="play" class="w-5 h-5 ml-0.5"></i>';
        window.LucideIcons?.initialize(playBtn);
        cancelAnimationFrame(animFrame);
        updateProgress();
    });

    audio.addEventListener('error', () => {
        playBtn.innerHTML = '<i data-lucide="circle-alert" class="w-5 h-5 text-red-400"></i>';
        window.LucideIcons?.initialize(playBtn);
        playBtn.disabled = true;
    });

    // Play/pause toggle
    playBtn.addEventListener('click', () => {
        if (audio.error) return;

        // Pause any other playing audio on the page
        document.querySelectorAll('.audio-player audio').forEach(a => {
            if (a !== audio && !a.paused) {
                a.pause();
                a.currentTime = 0;
            }
        });

        if (isPlaying) {
            audio.pause();
        } else {
            audio.play().catch(() => {
                playBtn.innerHTML = '<i data-lucide="circle-alert" class="w-5 h-5 text-red-400"></i>';
                window.LucideIcons?.initialize(playBtn);
            });
        }
    });

    function seekToClientX(clientX) {
        syncDurationFromAudio();
        if (!duration) return;
        const rect = waveformContainer.getBoundingClientRect();
        const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        seekToTime(percent * duration);
    }

    function seekToTime(seconds) {
        syncDurationFromAudio();
        if (!duration) return;
        const nextTime = Math.max(0, Math.min(duration, seconds));
        audio.currentTime = nextTime;
        currentTime = audio.currentTime;
        updateProgress();
    }

    waveformContainer.addEventListener('pointerdown', (e) => {
        if (e.button !== undefined && e.button !== 0) return;
        if (e.pointerType === 'touch' || e.pointerType === 'pen') {
            touchSeekPointer = {
                pointerId: e.pointerId,
                startX: e.clientX,
                startY: e.clientY,
            };
            return;
        }
        seekToClientX(e.clientX);
        pointerSeekClickUntil = Date.now() + POINTER_CLICK_SUPPRESSION_MS;
    });

    waveformContainer.addEventListener('pointermove', (e) => {
        if (!touchSeekPointer || touchSeekPointer.pointerId !== e.pointerId) return;
        const deltaX = Math.abs(e.clientX - touchSeekPointer.startX);
        const deltaY = Math.abs(e.clientY - touchSeekPointer.startY);
        if (deltaX > POINTER_TAP_THRESHOLD_PX || deltaY > POINTER_TAP_THRESHOLD_PX) {
            touchSeekPointer = null;
        }
    });

    waveformContainer.addEventListener('pointerup', (e) => {
        if (!touchSeekPointer || touchSeekPointer.pointerId !== e.pointerId) return;
        const deltaX = Math.abs(e.clientX - touchSeekPointer.startX);
        const deltaY = Math.abs(e.clientY - touchSeekPointer.startY);
        touchSeekPointer = null;
        if (deltaX > POINTER_TAP_THRESHOLD_PX || deltaY > POINTER_TAP_THRESHOLD_PX) return;
        seekToClientX(e.clientX);
        pointerSeekClickUntil = Date.now() + POINTER_CLICK_SUPPRESSION_MS;
    });

    waveformContainer.addEventListener('pointercancel', (e) => {
        if (!touchSeekPointer || touchSeekPointer.pointerId !== e.pointerId) return;
        touchSeekPointer = null;
    });

    waveformContainer.addEventListener('click', (e) => {
        if (Date.now() < pointerSeekClickUntil) return;
        seekToClientX(e.clientX);
    });

    waveformContainer.addEventListener('keydown', (e) => {
        syncDurationFromAudio();
        if (!duration) return;

        switch (e.key) {
            case 'ArrowLeft':
            case 'ArrowDown':
                e.preventDefault();
                seekToTime(currentTime - KEYBOARD_SEEK_STEP_SECONDS);
                break;
            case 'ArrowRight':
            case 'ArrowUp':
                e.preventDefault();
                seekToTime(currentTime + KEYBOARD_SEEK_STEP_SECONDS);
                break;
            case 'Home':
                e.preventDefault();
                seekToTime(0);
                break;
            case 'End':
                e.preventDefault();
                seekToTime(duration);
                break;
        }
    });

    // Chrome duration bug fix
    audio.addEventListener('durationchange', () => {
        syncDurationFromAudio();
        updateTimeDisplay();
    });

    // Initial render
    updateTimeDisplay();

    // Initialize Lucide icons
    window.LucideIcons?.initialize(container);
}

/**
 * Initialize all audio players on the page
 */
function initAllAudioPlayers(root) {
    const container = root || document;
    container.querySelectorAll('.audio-player:not([data-initialized])').forEach(el => {
        el.setAttribute('data-initialized', '1');
        initAudioPlayer(el);
    });
}

// Expose globally for dynamic content (new messages appended via AJAX)
window.initAudioPlayers = initAllAudioPlayers;

// Auto-initialize on page load
function autoInit() {
    initAllAudioPlayers();

    // Watch for dynamically added audio players (thread switching, AJAX loads)
    const messagesContainer = document.getElementById('threads_right');
    if (messagesContainer) {
        const observer = new MutationObserver(() => {
            if (document.querySelectorAll('.audio-player:not([data-initialized])').length > 0) {
                initAllAudioPlayers();
            }
        });
        observer.observe(messagesContainer, { childList: true, subtree: true });
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
} else {
    autoInit();
}
